import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Badge from "aldoo-ra/Badge"
import NewsletterAPI from "../api"
import { 
  PlayIcon, 
  ArrowPathIcon, 
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon 
} from "@heroicons/react/24/outline"

const NewsletterDiagnostics = () => {
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState([])

  const addResult = (type, message, details = null) => {
    const timestamp = new Date().toLocaleTimeString()
    const uniqueId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    setResults(prev => [{
      id: uniqueId,
      type,
      message,
      details,
      timestamp
    }, ...prev.slice(0, 9)]) // Keep only last 10 results
  }

  const triggerScheduler = async () => {
    try {
      setLoading(true)
      addResult('info', 'Triggering newsletter scheduler...')
      
      const response = await NewsletterAPI.triggerScheduler()
      
      if (response.success) {
        addResult('success', 'Newsletter scheduler completed successfully', response.message)
      } else {
        addResult('error', 'Scheduler failed', response.error)
      }
    } catch (error) {
      addResult('error', 'Failed to trigger scheduler', error.message)
    } finally {
      setLoading(false)
    }
  }

  const resetRunningReleases = async () => {
    try {
      setLoading(true)
      addResult('info', 'Resetting running releases...')

      // First get running releases
      const releasesResponse = await NewsletterAPI.getReleases({
        match: { status: { $in: ['running', 'paused'] } }
      })

      if (releasesResponse.success && releasesResponse.releases?.length > 0) {
        let resetCount = 0
        
        for (const release of releasesResponse.releases) {
          try {
            const resetResponse = await NewsletterAPI.resetRelease(release._id)
            if (resetResponse.success) {
              resetCount++
              addResult('success', `Reset release: ${release.name}`)
            } else {
              addResult('warning', `Failed to reset: ${release.name}`, resetResponse.error)
            }
          } catch (error) {
            addResult('error', `Error resetting: ${release.name}`, error.message)
          }
        }
        
        addResult('success', `Reset completed: ${resetCount} releases reset`)
      } else {
        addResult('info', 'No running or paused releases found to reset')
      }
    } catch (error) {
      addResult('error', 'Failed to reset releases', error.message)
    } finally {
      setLoading(false)
    }
  }

  const getBadgeVariant = (type) => {
    switch (type) {
      case 'success': return 'success'
      case 'error': return 'error'
      case 'warning': return 'warning'
      default: return 'info'
    }
  }

  const getIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircleIcon className="w-4 h-4" />
      case 'error': return <ExclamationTriangleIcon className="w-4 h-4" />
      case 'warning': return <ExclamationTriangleIcon className="w-4 h-4" />
      default: return <WrenchScrewdriverIcon className="w-4 h-4" />
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <Typography variant="admin_h3" className="mb-2">Newsletter System Diagnostics</Typography>
        <Typography variant="admin_body" className="text-admin_text_secondary">
          Manual controls for testing and debugging newsletter releases
        </Typography>
      </div>

      {/* Control Panel */}
      <div className="bg-admin_canvas p-6 rounded-lg">
        <Typography variant="admin_h4" className="mb-4">System Controls</Typography>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <Typography variant="admin_txt1" className="font-medium">Process Running Releases</Typography>
            <Typography variant="admin_txt2" className="text-admin_text_secondary mb-3">
              Manually trigger the newsletter scheduler to process any running releases and send email batches.
            </Typography>
            <Button 
              variant="admin_primary" 
              onClick={triggerScheduler}
              disabled={loading}
              className="w-full"
            >
              <PlayIcon className="w-4 h-4 mr-2" />
              Trigger Scheduler
            </Button>
          </div>

          <div className="space-y-3">
            <Typography variant="admin_txt1" className="font-medium">Reset Running Releases</Typography>
            <Typography variant="admin_txt2" className="text-admin_text_secondary mb-3">
              Reset all running and paused releases back to scheduled status for testing.
            </Typography>
            <Button 
              variant="admin_secondary" 
              onClick={resetRunningReleases}
              disabled={loading}
              className="w-full"
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              Reset All Running
            </Button>
          </div>
        </div>
      </div>

      {/* Results Log */}
      <div className="bg-admin_canvas p-6 rounded-lg">
        <Typography variant="admin_h4" className="mb-4">Operation Log</Typography>
        
        {results.length === 0 ? (
          <div className="text-center py-8">
            <WrenchScrewdriverIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-4" />
            <Typography variant="admin_body" className="text-admin_text_secondary">
              No operations performed yet. Use the controls above to test the newsletter system.
            </Typography>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {results.map((result) => (
              <div key={result.id} className="flex items-start space-x-3 p-3 bg-admin_background rounded border">
                <div className="flex-shrink-0 mt-0.5">
                  <Badge variant={getBadgeVariant(result.type)} className="flex items-center space-x-1">
                    {getIcon(result.type)}
                  </Badge>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <Typography variant="admin_txt1" className="font-medium">
                      {result.message}
                    </Typography>
                    <Typography variant="admin_txt2" className="text-admin_text_secondary text-xs">
                      {result.timestamp}
                    </Typography>
                  </div>
                  
                  {result.details && (
                    <Typography variant="admin_txt2" className="text-admin_text_secondary mt-1">
                      {result.details}
                    </Typography>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Information Panel */}
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
        <Typography variant="admin_txt1" className="font-medium text-blue-800 mb-2">
          Testing Instructions
        </Typography>
        <div className="text-blue-700 space-y-1">
          <Typography variant="admin_txt2">
            1. Start a newsletter release from the Releases page
          </Typography>
          <Typography variant="admin_txt2">
            2. Use "Trigger Scheduler" to manually process email batches
          </Typography>
          <Typography variant="admin_txt2">
            3. Check the Activity page to see sent mock emails
          </Typography>
          <Typography variant="admin_txt2">
            4. Use "Reset All Running" to reset releases for repeated testing
          </Typography>
        </div>
      </div>
    </div>
  )
}

export default NewsletterDiagnostics